// src/features/assistant-connect/i18n/en.ts

export default {
  name: 'AI assistant',
  entry: 'AI assistant',
  entryTooltip: 'Connect your AI assistant to Insight',
  title: 'Connect your AI assistant',
  subtitle: 'Query Insight by talking to the assistant you already use.',
  accessDenied: 'Connecting an assistant is not available for your profile.',

  how: {
    title: 'How it works',
    account:
      'The assistant works with your Insight account: it acts as you and sees the same properties you see in these screens, not one more.',
    readOnly:
      'In this version it only reads: it can look things up, it changes nothing. Your Insight data is not modified from the assistant.',
    consent:
      'The first time, the Roommatik sign-in page opens, then a screen that says «Va a conectar <assistant> con su cuenta de Roommatik Insight…» (you are about to connect <assistant> to your Roommatik Insight account): accept it to finish.',
    disconnect:
      "You can disconnect it at any time from your assistant's connector settings; each tab says where. Once disconnected, the assistant stops using your account.",
  },

  environment: {
    test: 'This is the test Insight: the server address connects the assistant to the test environment, not to production.',
    unknown: "This Insight's address is not recognised; the server address shown is the test environment's.",
  },

  copy: 'Copy',
  copied: 'Copied',

  connection: {
    title: 'Connection details',
    serverUrl: 'Server address',
    clientId: 'Client ID',
    secret: 'Client secret: leave it blank.',
  },

  tabsLabel: 'Assistants',
  requirements: 'Requirements',
  disconnectLabel: 'To disconnect it',

  assistants: {
    claude: {
      name: 'Claude',
      disconnect:
        'In claude.ai, «Settings» › «Connectors»: pick the connector and disconnect or remove it. In Claude Code, type «claude mcp remove insight» in your terminal.',
      sections: {
        apps: {
          title: 'Claude on the web, desktop and mobile',
          requirements:
            'Free (one custom connector), Pro and Max plans. On Team and Enterprise an organisation owner adds it in the organisation settings; each member then connects.',
          step1: 'In claude.ai, open «Settings» › «Connectors» and click «Add custom connector».',
          step2: 'Type a name (for example, Roommatik Insight) and paste the server address.',
          step3: 'Open «Advanced settings», paste the client ID, leave the secret blank and click «Add».',
          step4:
            'Click «Connect» and sign in with your Insight account. The desktop and mobile apps use the same connector, with no further setup.',
          note: 'The sign-in details cannot be changed once the connector is added: if you make a mistake, remove it and add it again.',
        },
        code: {
          title: 'Claude Code',
          requirements: 'Any plan that includes Claude Code.',
          step1: 'Type this command in your terminal (it already carries the address and the client ID):',
          step2: 'In Claude Code, type /mcp, pick «insight» and choose «Authenticate»: the browser opens for you to sign in.',
        },
      },
    },
    chatgpt: {
      name: 'ChatGPT',
      disconnect: '«Settings» › «Apps & Connectors»: pick the app and disconnect or delete it.',
      sections: {
        web: {
          title: 'ChatGPT on the web',
          requirements:
            'Web only. For it to make changes you need ChatGPT Business, Enterprise or Edu, with developer mode turned on by an admin. On Pro it can only read. For Plus, OpenAI states no availability.',
          step1:
            'On Business, Enterprise or Edu, ask an admin of your workspace to turn on developer mode; they can also publish the app to the whole team.',
          step2: 'In chatgpt.com, open «Settings» › «Apps & Connectors» › «Advanced settings», turn on «Developer mode» and click «Create».',
          step3: 'Type a name, paste the server address and choose «OAuth» authentication.',
          step4:
            'In the advanced options, choose a client you define («User-Defined OAuth Client»), paste the client ID, leave the secret blank and save.',
          step5: 'Sign in with your Insight account when asked, and pick the app in a new conversation.',
        },
      },
    },
    copilot: {
      name: 'GitHub Copilot in VS Code',
      disconnect: 'In the command palette, «MCP: List Servers»: pick insight and remove it.',
      sections: {
        vscode: {
          title: 'GitHub Copilot in Visual Studio Code',
          requirements:
            'Copilot Free, Pro and Pro+ allow it. On Copilot Business and Enterprise, an organisation admin must turn on the «MCP servers in Copilot» policy, which is off by default.',
          step1: 'Open the command palette (Ctrl+Shift+P) and run «MCP: Add Server».',
          step2: 'Choose «HTTP», paste the server address and name it insight. If you prefer, write it in the mcp.json file:',
          step3: 'When it asks for a client ID, paste the one on this tab, leave the secret blank and sign in in the browser.',
          step4: 'Open Copilot chat in «Agent» mode and ask.',
        },
      },
    },
    cursor: {
      name: 'Cursor',
      disconnect: '«Cursor Settings» › «MCP»: switch off or delete insight.',
      sections: {
        ide: {
          title: 'Cursor',
          requirements: 'Cursor states no plan requirement. In teams, an admin can restrict which servers are allowed.',
          step1: 'Open «Cursor Settings» › «MCP» and click «Add new MCP server»: the mcp.json file opens.',
          step2: 'Add this block, which already carries the address and the client ID, and save:',
          step3: 'Back in the settings, click «Connect» next to insight and sign in in the browser.',
        },
      },
    },
  },

  tasks: {
    title: 'What you can ask',
    subtitle: 'Write in your own words, as you would ask a colleague. With your profile you can ask it to:',
    items: {
      properties: {
        title: 'Show your properties',
        summary: 'Which properties you can look at and which chain each belongs to.',
      },
      indicators: {
        title: "Look up a property's indicators",
        summary: 'The same Insight indicators for the period you name.',
      },
      comparisons: {
        title: 'Compare periods and properties',
        summary: 'How this month is doing against the last one, or which property performs best on an indicator.',
      },
      arrivals: {
        title: "Show today's arrivals",
        summary: 'How many arrivals there are, how many have checked in and how many are left.',
      },
      checkIns: {
        title: 'Review check-ins',
        summary: "A period's check-ins and a reservation's journey, with personal data hidden.",
      },
      alerts: {
        title: 'Review alerts and errors',
        summary: 'Which alerts are open and what an error code means.',
      },
      devices: {
        title: 'Check the state of the devices',
        summary: 'Which devices are connected, which do not respond and since when.',
      },
      manual: {
        title: 'Look things up in the manual',
        summary: 'How something is done in Insight, with the page that explains it.',
      },
    },
  },
};
